import api from './client';
import axios from 'axios';
import type { Pictogram, Template, Page, PaginatedResponse, PictogramCounts, PresignedUploadResponse, UserProfile, AdminUser, Registration, LoginLogEntry, Team, Organization } from '../types';

// Pictograms
export const listPictograms = (params?: Record<string, string>) =>
  api.get<PaginatedResponse<Pictogram>>('/pictograms', { params }).then((r) => r.data);

export const countPictograms = (params?: Record<string, string>) =>
  api.get<PictogramCounts>('/pictograms/counts', { params }).then((r) => r.data);

export const getPictogram = (id: string) =>
  api.get<Pictogram>(`/pictograms/${id}`).then((r) => r.data);

export const createPictogram = (data: Partial<Pictogram>) =>
  api.post<Pictogram>('/pictograms', data).then((r) => r.data);

export const updatePictogram = (id: string, data: Partial<Pictogram>) =>
  api.put<Pictogram>(`/pictograms/${id}`, data).then((r) => r.data);

export const deletePictogram = (id: string) =>
  api.delete(`/pictograms/${id}`).then((r) => r.data);

export const generatePictogram = (id: string, designStyle?: 'standard' | 'standard-plus' | 'aac') =>
  api.post<{ status: string; pictogramId: string }>(`/pictograms/${id}/generate`, { designStyle }).then((r) => r.data);

// Upload
export const createPresignedUpload = (fileName: string, contentType: string, prefix?: string) =>
  api.post<PresignedUploadResponse>('/uploads/presign', { fileName, contentType, prefix }).then((r) => r.data);

// Templates
export const listTemplates = (params?: Record<string, string>) =>
  api.get<PaginatedResponse<Template>>('/templates', { params }).then((r) => r.data);

export const createTemplate = (data: Partial<Template>) =>
  api.post<Template>('/templates', data).then((r) => r.data);

// Pages
export const listPages = (params?: Record<string, string>) =>
  api.get<PaginatedResponse<Page>>('/pages', { params }).then((r) => r.data);

export const getPage = (id: string) =>
  api.get<Page>(`/pages/${id}`).then((r) => r.data);

export const savePage = (data: Partial<Page>) =>
  data.id
    ? api.put<Page>(`/pages/${data.id}`, data).then((r) => r.data)
    : api.post<Page>('/pages', data).then((r) => r.data);

export const deletePage = (id: string) =>
  api.delete(`/pages/${id}`).then((r) => r.data);

export const renameFolderApi = (oldName: string, newName: string) =>
  api.post<{ updated: number }>('/pages/rename-folder', { oldName, newName }).then((r) => r.data);

export const duplicatePage = (id: string) =>
  api.post<Page>(`/pages/${id}/duplicate`).then((r) => r.data);

// --- Profile ---
export const getProfile = () =>
  api.get<UserProfile>('/profile').then((r) => r.data);

export const updateProfile = (data: Partial<UserProfile> & { targetUserId?: string }) =>
  api.put<UserProfile>('/profile', data).then((r) => r.data);

export const getPublicProfile = (userId: string) =>
  api.get<UserProfile>(`/users/${userId}/profile`).then((r) => r.data);

// --- Admin: Users ---
export const listUsers = () =>
  api.get<{ users: AdminUser[] }>('/admin/users').then((r) => r.data);

export const createUser = (data: {
  email: string;
  displayName?: string;
  organization?: string;
  teamId?: string;
  teamIds?: string[];
  visibleSources?: string[];
  role: string;
  password?: string;
  permanentPassword?: boolean;
  aiGenerationEnabled?: boolean;
  aiGenerationLimit?: number;
}) =>
  api.post('/admin/users', data).then((r) => r.data);

export const updateUserRole = (userId: string, role: string) =>
  api.put(`/admin/users/${userId}/role`, { role }).then((r) => r.data);

export const disableUser = (userId: string, disabled: boolean) =>
  api.post(`/admin/users/${userId}/disable`, { disabled }).then((r) => r.data);

export const deleteUserApi = (userId: string) =>
  api.delete(`/admin/users/${userId}`).then((r) => r.data);

// --- Admin: Registrations ---
export const listRegistrations = (status = 'pending') =>
  api.get<{ items: Registration[] }>('/admin/registrations', { params: { status } }).then((r) => r.data);

export const reviewRegistration = (id: string, action: 'approve' | 'reject', role?: string) =>
  api.put(`/admin/registrations/${id}`, { action, role }).then((r) => r.data);

// --- Public: Registration (no auth) ---
export const requestRegistration = (data: { email: string; displayName: string; organization?: string; message?: string }) =>
  axios.post(`${import.meta.env.VITE_API_URL}/register`, data).then((r) => r.data);

// --- Login Log ---
export const recordLogin = () =>
  api.post('/auth/log-login').then((r) => r.data);

export const listLoginLogs = (limit = 100) =>
  api.get<{ items: LoginLogEntry[] }>('/admin/login-log', { params: { limit } }).then((r) => r.data);

// --- Admin: Teams & Einrichtungen ---
export const listTeamsAndOrgs = () =>
  api.get<{ teams: Team[]; organizations: Organization[] }>('/admin/teams').then((r) => r.data);

export const saveTeamOrOrg = (data: Record<string, unknown>) =>
  data.id
    ? api.put<Team | Organization>(`/admin/teams/${data.id}`, data).then((r) => r.data)
    : api.post<Team | Organization>('/admin/teams', data).then((r) => r.data);

export const deleteTeamOrOrg = (id: string) =>
  api.delete(`/admin/teams/${id}`).then((r) => r.data);

// --- Admin: KI-Nutzung ---
export interface AIGeneration {
  id: string;
  title: string;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  generationModel?: string;
  generationQuality?: string;
  generationSize?: string;
  generationDurationMs?: number;
  generationCostUsd?: number;
}

export interface AIUsageResponse {
  generations: AIGeneration[];
  totalCount: number;
  totalCostUsd: number;
  monthly: Array<{ month: string; count: number; costUsd: number }>;
  openaiCosts: Array<{ date: string; lineItem: string | null; amountUsd: number }>;
  openaiTotalCostUsd: number;
  openaiCreditBalance: number | null;
  openaiCreditLoaded: number | null;
  openaiUsage: Array<{ date: string; model: string | null; images: number; requests: number }>;
}

export const getAIUsage = () =>
  api.get<AIUsageResponse>('/admin/ai-usage').then((r) => r.data);
